import { useEffect } from "react";

// Minimal poll-on-an-interval hook. Each tick calls `fetcher()`, then
// schedules the next tick `intervalMs` later (default 5s). Stops when:
// - the component unmounts (cancelled flag short-circuits in-flight ticks),
// - `shouldStop(data)` returns true after a successful fetch.
//
// `deps` is the list of values that, when they change, restart the
// poll cycle (e.g. a workflowId switching between pages). It mirrors
// the standard React useEffect deps array — pass `[]` for a one-time
// polling loop tied to the component's lifetime.
//
// Errors are surfaced via `onError`; the poll continues unless
// `shouldStop` returns true. Callers that want to drop the page into
// an error state on failure should setX(...) inside onError.
export function usePolling<T>(opts: {
  fetcher: () => Promise<T>;
  onData: (data: T) => void;
  intervalMs?: number;
  shouldStop?: (data: T) => boolean;
  onError?: (err: unknown) => void;
  deps?: unknown[];
}): void {
  const intervalMs = opts.intervalMs ?? 5000;
  const deps = opts.deps ?? [];
  // The hook intentionally captures the latest opts via the deps list
  // the caller passes — re-running the effect only when those values
  // change. ESLint's exhaustive-deps would want every closed-over
  // value, which defeats the point of the hook.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await opts.fetcher();
        if (cancelled) return;
        opts.onData(r);
        if (opts.shouldStop?.(r)) return;
      } catch (err: unknown) {
        if (cancelled) return;
        opts.onError?.(err);
      }
      if (!cancelled) setTimeout(tick, intervalMs);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, deps);
  /* eslint-enable react-hooks/exhaustive-deps */
}
